import React, { useState, useEffect } from 'react';
import { widgetTemplates } from './widgetTemplates';
import { useCustomTemplates } from './hooks/useCustomTemplates';
import MiniWidgetPreview from './WidgetEditComponents/MiniWidgetPreview';
import './AddWidgets.css';

const categoryIcons = {
  'All': '/img/night.svg',
  'Fancy': '/img/red-carpet.svg',
  'Basic': '/img/blocks.svg',
  'Button': '/img/push.svg',
  'Video': '/img/camera.svg',
  'Image': '/img/cam.svg',
  'Bonus': '/img/launch.svg',
};

function DraggableButton({ widgetType, onAddWidget }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    // Bereinige Widget-Template für Firebase (entferne aiInfo)
    const cleanWidget = { ...widgetTemplates[widgetType] };
    
    // Entferne Firebase-incompatible Felder
    delete cleanWidget.aiInfo;
    
    // Entferne problematische Keys mit Punkten
    Object.keys(cleanWidget).forEach(key => {
      if (key.includes('.') || key.includes('#') || key.includes('$') || key.includes('/') || key.includes('[') || key.includes(']')) {
        delete cleanWidget[key];
      }
    });
    
    onAddWidget(cleanWidget);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 2000);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <button
      onClick={handleClick}
      className={`widget-button widget-${widgetType} ${isAnimating ? 'clicked' : ''}`}
      style={{ background: 'none', padding: '0', borderRadius: '10px', margin: '0' }}
    >
      <div className="image-container">
        {widgetTemplates[widgetType].imagePreview && !imageError ? (
          <img
            className="widget-preview"
            src={widgetTemplates[widgetType].imagePreview}
            alt={`Vorschau von ${widgetType}`}
            onError={handleImageError}
          />
        ) : (
          <div className="widget-overlay-text-placeholder">
            <div className="placeholder-icon">
              <i className="fas fa-puzzle-piece"></i>
            </div>
            <div className="placeholder-text">{widgetTemplates[widgetType].name || widgetType}</div>
            <div className="placeholder-subtext">Widget Preview</div>
          </div>
        )}
        <div className="widget-overlay">
          <img src="/img/plus_1.svg" alt="" />
          <span className="widget-overlay-text">Click to add me</span>
        </div>
      </div>
      <p style={{ marginTop: '5px', marginBottom: '30px', fontSize: '12px', textTransform: 'uppercase', color: 'gray', letterSpacing: '2px' }}>
        {widgetTemplates[widgetType].name}
      </p>
    </button>
  );
}

// Custom Template Button Component
function CustomTemplateButton({ template, onAddWidget, onDeleteTemplate, canDelete = false, colors = {}, fonts = {} }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = () => {
    // Bereinige Custom Template für Firebase
    const cleanWidget = { 
      ...template.widgetData,
      type: template.originalWidgetType, // Use original widget type for rendering
      name: template.templateName // Custom name
    };
    
    // Entferne interne Properties
    delete cleanWidget.templateId;
    delete cleanWidget.userId;
    delete cleanWidget.createdAt;
    delete cleanWidget.updatedAt;
    delete cleanWidget.isPublic;
    delete cleanWidget.usageCount;
    
    // ✅ AUTO-APPLY BRAND COLORS & FONTS - KOMPLETT ERSETZEN!
    const applyBrandStyling = (widget) => {
      console.log('🎨 [TEMPLATE BRANDING] Starting brand styling application:', {
        templateName: template.templateName,
        widgetType: template.originalWidgetType,
        hasColors: !!colors && Object.keys(colors).length > 0,
        hasFonts: !!fonts && (!!fonts.globalBigFont || !!fonts.globalSmallFont),
        colors: colors,
        fonts: fonts,
        hasContainerSections: !!widget.containerSections
      });
      
      const branded = { ...widget };
      
      // 🎨 BRAND COLORS ANWENDEN - ALLE Farben ersetzen!
      if (colors && Object.keys(colors).length > 0) {
        // Widget-Level Background - IMMER ersetzen
        branded.backgroundColor = colors.color4 || colors.color5 || '#f9f9f9';
        
        // Container Sections Background Colors - IMMER ersetzen
        if (branded.containerSections && branded.containerSections.length > 0) {
          branded.containerSections = branded.containerSections.map((container, index) => {
            const updatedContainer = { ...container };
            
            // JEDER Container bekommt eine Brand-Color (alternierend für Abwechslung)
            updatedContainer.containerBackgroundColor = index === 0 
              ? (colors.color4 || '#f9f9f9')  // Erster Container: hellere Farbe
              : (colors.color5 || '#ffffff'); // Zweiter Container: noch heller
            
            // Content Elements - Farben in Texten, Buttons, etc. ersetzen
            if (updatedContainer.contentElements && updatedContainer.contentElements.length > 0) {
              updatedContainer.contentElements = updatedContainer.contentElements.map((element) => {
                // Text Elements - ALLE Farben komplett ersetzen
                if (element.type === 'text') {
                  const updatedTextElement = {
                    ...element,
                    color: colors.color2 || colors.color1 || '#333333' // Dunkle Brand-Color für Text
                  };
                  
                  // ✅ Entferne alte Farb-Properties
                  delete updatedTextElement.textColor;
                  delete updatedTextElement.titleColor;
                  delete updatedTextElement.descriptionColor;
                  delete updatedTextElement.subtitleColor;
                  
                  // ✅ ROBUST: Entferne ALLE inline styles aus Content (Farben, Fonts, etc.)
                  if (updatedTextElement.content && typeof updatedTextElement.content === 'string') {
                    let cleaned = updatedTextElement.content;
                    
                    // Strategie: Entferne ALLE style attributes komplett
                    cleaned = cleaned
                      .replace(/<span[^>]*style="[^"]*"[^>]*>/gi, '<span>')
                      .replace(/<span[^>]*style='[^']*'[^>]*>/gi, '<span>')
                      .replace(/<span>\s*<\/span>/gi, '')
                      .replace(/<span><\/span>/gi, '')
                      .replace(/<span>\s*<span>/gi, '<span>');
                    
                    if (cleaned !== updatedTextElement.content) {
                      updatedTextElement.content = cleaned;
                      console.log(`🧹 [COLOR] Cleaned inline styles from content`);
                    }
                  }
                  
                  return updatedTextElement;
                }
                
                // Button Elements - Farben ersetzen
                if (element.type === 'button') {
                  return {
                    ...element,
                    buttonColor: colors.color1 || colors.color2 || '#5C80BC', // Brand-Color für Button BG
                    buttonTextColor: '#FFFFFF', // Weiß für Kontrast
                    buttonBorderColor: colors.color1 || colors.color2 || '#5C80BC'
                  };
                }
                
                return element;
              });
            }
            
            return updatedContainer;
          });
        }
      }
      
      // 🔤 BRAND FONTS ANWENDEN - ALLE Fonts ersetzen!
      if (fonts && (fonts.globalBigFont || fonts.globalSmallFont)) {
        console.log('🔤 [TEMPLATE FONTS] Applying brand fonts:', {
          primaryFont: fonts.globalBigFont,
          secondaryFont: fonts.globalSmallFont
        });
        
        if (branded.containerSections) {
          branded.containerSections = branded.containerSections.map((container, containerIndex) => {
            if (container.contentElements && container.contentElements.length > 0) {
              const updatedElements = container.contentElements.map((element, elementIndex) => {
                // Text Elements - IMMER Brand-Font verwenden
                if (element.type === 'text') {
                  // 🎯 Smart Font Detection:
                  // 1. Nutze elementType wenn vorhanden (h1, h2, h3, p, etc.)
                  // 2. Fallback auf fontSize (größer als 24px = Primary Font)
                  // 3. Fallback auf Position (erstes Element = Primary Font)
                  
                  const elementType = element.elementType?.toLowerCase();
                  const fontSize = parseInt(element.fontSize) || 16;
                  const isFirstElement = elementIndex === 0;
                  
                  const shouldUsePrimaryFont = 
                    elementType === 'h1' || 
                    elementType === 'h2' || 
                    fontSize > 24 || 
                    (isFirstElement && fontSize > 18);
                  
                  const selectedFont = shouldUsePrimaryFont 
                    ? (fonts.globalBigFont || 'Voyage') 
                    : (fonts.globalSmallFont || 'Jost');
                  
                  console.log(`  📝 [Container ${containerIndex}][Element ${elementIndex}] Text Element:`, {
                    elementType: element.elementType,
                    fontSize: element.fontSize,
                    oldFont: element.fontFamily,
                    newFont: selectedFont,
                    usePrimary: shouldUsePrimaryFont,
                    contentType: typeof element.content,
                    contentPreview: typeof element.content === 'string' ? element.content.substring(0, 100) : 'NOT A STRING'
                  });
                  
                  // ✅ CRITICAL: Setze fontFamily UND bereinige Content
                  const updatedTextElement = {
                    ...element,
                    fontFamily: selectedFont
                  };
                  
                  // ✅ ROBUST: Entferne ALLE inline font-family + color styles aus Content
                  // Inline styles überschreiben element.fontFamily im DOM!
                  if (updatedTextElement.content && typeof updatedTextElement.content === 'string') {
                    let cleaned = updatedTextElement.content;
                    
                    // Strategie: Entferne ALLE style attributes komplett, dann setzen wir alles über element properties
                    cleaned = cleaned
                      // 1. Entferne komplette style attributes
                      .replace(/<span[^>]*style="[^"]*"[^>]*>/gi, '<span>')
                      .replace(/<span[^>]*style='[^']*'[^>]*>/gi, '<span>')
                      // 2. Entferne leere spans
                      .replace(/<span>\s*<\/span>/gi, '')
                      .replace(/<span><\/span>/gi, '')
                      // 3. Cleanup: mehrfache spans
                      .replace(/<span>\s*<span>/gi, '<span>');
                    
                    if (cleaned !== updatedTextElement.content) {
                      updatedTextElement.content = cleaned;
                      console.log(`    🧹 Cleaned inline styles from content`);
                    }
                  }
                  
                  return updatedTextElement;
                }
                
                // Button Elements - IMMER Brand-Font verwenden (Secondary Font)
                if (element.type === 'button') {
                  const selectedFont = fonts.globalSmallFont || 'Jost';
                  
                  console.log(`  🔘 [Container ${containerIndex}][Element ${elementIndex}] Button Element:`, {
                    oldFont: element.buttonFontFamily,
                    newFont: selectedFont
                  });
                  
                  return {
                    ...element,
                    buttonFontFamily: selectedFont
                  };
                }
                
                return element;
              });
              
              return {
                ...container,
                contentElements: updatedElements
              };
            }
            return container;
          });
        }
        
        console.log('✅ [TEMPLATE FONTS] Brand fonts successfully applied to containerSections!');
      } else {
        console.warn('⚠️ [TEMPLATE FONTS] No brand fonts available - using template default fonts');
      }
      
      // 🔤 LEGACY SUPPORT: Apply fonts to old-style widgets without containerSections
      // (e.g. older SimplisticQuote, SimplisticServices with direct fontFamily properties)
      if (fonts && (fonts.globalBigFont || fonts.globalSmallFont)) {
        // Check for legacy title/subtitle fonts
        if (branded.titleFontFamily !== undefined) {
          const oldTitleFont = branded.titleFontFamily;
          branded.titleFontFamily = fonts.globalBigFont || 'Voyage';
          console.log('  📝 [LEGACY] Title Font:', { old: oldTitleFont, new: branded.titleFontFamily });
        }
        if (branded.subtitleFontFamily !== undefined) {
          const oldSubtitleFont = branded.subtitleFontFamily;
          branded.subtitleFontFamily = fonts.globalSmallFont || 'Jost';
          console.log('  📝 [LEGACY] Subtitle Font:', { old: oldSubtitleFont, new: branded.subtitleFontFamily });
        }
        if (branded.descriptionFontFamily !== undefined) {
          const oldDescFont = branded.descriptionFontFamily;
          branded.descriptionFontFamily = fonts.globalSmallFont || 'Jost';
          console.log('  📝 [LEGACY] Description Font:', { old: oldDescFont, new: branded.descriptionFontFamily });
        }
        if (branded.buttonFontFamily !== undefined) {
          const oldButtonFont = branded.buttonFontFamily;
          branded.buttonFontFamily = fonts.globalSmallFont || 'Jost';
          console.log('  🔘 [LEGACY] Button Font:', { old: oldButtonFont, new: branded.buttonFontFamily });
        }
        
        // Legacy QA Widget items
        if (branded.items && Array.isArray(branded.items)) {
          branded.items = branded.items.map((item, index) => {
            const updatedItem = { ...item };
            if (item.questionFontFamily !== undefined) {
              updatedItem.questionFontFamily = fonts.globalBigFont || 'Voyage';
            }
            if (item.answerFontFamily !== undefined) {
              updatedItem.answerFontFamily = fonts.globalSmallFont || 'Jost';
            }
            return updatedItem;
          });
          console.log(`  📋 [LEGACY] Updated ${branded.items.length} QA items with brand fonts`);
        }
        
        // Legacy Testimonial Widget items
        if (branded.testimonials && Array.isArray(branded.testimonials)) {
          branded.testimonials = branded.testimonials.map((testimonial, index) => {
            const updatedTestimonial = { ...testimonial };
            if (testimonial.nameFontFamily !== undefined) {
              updatedTestimonial.nameFontFamily = fonts.globalBigFont || 'Voyage';
            }
            if (testimonial.textFontFamily !== undefined) {
              updatedTestimonial.textFontFamily = fonts.globalSmallFont || 'Jost';
            }
            return updatedTestimonial;
          });
          console.log(`  💬 [LEGACY] Updated ${branded.testimonials.length} testimonials with brand fonts`);
        }
      }
      
      console.log('✅ [TEMPLATE BRANDING] Brand styling complete!', {
        widgetType: branded.type,
        containerSectionsCount: branded.containerSections?.length || 0,
        totalElements: branded.containerSections?.reduce((sum, c) => sum + (c.contentElements?.length || 0), 0) || 0
      });
      
      return branded;
    };
    
    const brandedWidget = applyBrandStyling(cleanWidget);
    
    console.log('🚀 [TEMPLATE ADD] Adding branded widget to page:', {
      templateName: template.templateName,
      widgetType: brandedWidget.type,
      brandingApplied: true
    });
    
    onAddWidget(brandedWidget);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 2000);
  };

  const handleDelete = async (e) => {
    e.stopPropagation(); // Prevent triggering the add widget action
    
    if (!window.confirm(`Are you sure you want to delete "${template.templateName}"?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteTemplate(template.templateId);
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`widget-button custom-template-button ${isAnimating ? 'clicked' : ''}`}
      style={{ background: 'none', padding: '0', borderRadius: '10px', margin: '0' }}
    >
      <div className="custom-template-image-container">
        {/* Always show mini widget preview - much better than static images */}
        <div className="custom-template-mini-preview-container">
          <MiniWidgetPreview 
            widgetData={{
              ...template.widgetData,
              type: template.originalWidgetType
            }}
            colors={colors}
            fonts={fonts}
          />
        </div>
        <div className="custom-template-overlay">
          <img src="/img/plus_1.svg" alt="" />
          <span className="custom-template-overlay-text">Click to add me</span>
        </div>
        {/* Delete Button - only visible on hover and if user can delete */}
        {canDelete && (
          <button 
            className="template-delete-btn"
            onClick={handleDelete}
            disabled={isDeleting}
            title="Delete Template (Only you can delete your templates)"
          >
            <i className={isDeleting ? "fas fa-spinner fa-spin" : "fas fa-trash"}></i>
          </button>
        )}
      </div>
      <p style={{ marginTop: '5px', marginBottom: '30px', fontSize: '12px', textTransform: 'uppercase', color: 'gray', letterSpacing: '2px' }}>
        {template.templateName}
      </p>
    </button>
  );
}

function AddWidgets({ onAddWidget, products = [], selectedProductId = '', onProductSelection, spaceId, colors = {}, fonts = {} }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [currentMode, setCurrentMode] = useState('Design');
  
  // 🔍 DEBUG: Check if fonts are received
  useEffect(() => {
    console.log('🔤 [ADD WIDGETS] Fonts prop received:', {
      fonts: fonts,
      hasGlobalBigFont: !!fonts?.globalBigFont,
      hasGlobalSmallFont: !!fonts?.globalSmallFont,
      globalBigFont: fonts?.globalBigFont,
      globalSmallFont: fonts?.globalSmallFont,
      fontsType: typeof fonts,
      fontsKeys: fonts ? Object.keys(fonts) : []
    });
  }, [fonts]);
  
  // 🔍 DEBUG: Log when component mounts
  useEffect(() => {
    console.log('🚀 [ADD WIDGETS] Component mounted with fonts:', {
      initialFonts: fonts,
      spaceId: spaceId
    });
  }, []);
  
  // ✅ CUSTOM TEMPLATES HOOK - with spaceId for color application
  const { customTemplates, loading: templatesLoading, deleteTemplate, canDeleteTemplate } = useCustomTemplates(spaceId);
  
  // 🛡️ SAFETY: Prevent infinite loading state with timeout
  const [safeLoading, setSafeLoading] = useState(true);
  
  useEffect(() => {
    // Set a maximum loading time of 10 seconds
    const loadingTimeout = setTimeout(() => {
      if (templatesLoading) {
        console.warn('⚠️ Custom templates loading timeout reached, forcing end of loading state');
        setSafeLoading(false);
      }
    }, 10000);
    
    // Clear timeout if loading finishes normally
    if (!templatesLoading) {
      setSafeLoading(false);
      clearTimeout(loadingTimeout);
    } else {
      setSafeLoading(true);
    }
    
    return () => clearTimeout(loadingTimeout);
  }, [templatesLoading]);
  
  // Use safe loading state that includes timeout protection
  const isActuallyLoading = templatesLoading && safeLoading;
  
  // 🔍 DEBUG: Log loading state
  useEffect(() => {
    console.log('🐛 Custom Templates Debug:', {
      templatesLoading,
      safeLoading,
      isActuallyLoading,
      templatesCount: customTemplates.length,
      spaceId,
      timestamp: new Date().toISOString()
    });
  }, [templatesLoading, safeLoading, isActuallyLoading, customTemplates.length, spaceId]);

  useEffect(() => {
    const extractedCategories = Object.values(widgetTemplates).reduce((acc, { cat1, cat2 }) => {
      if (cat1 && !acc.includes(cat1)) acc.push(cat1);
      if (cat2 && !acc.includes(cat2)) acc.push(cat2);
      return acc;
    }, ['All']);
    setCategories(extractedCategories);
  }, []);

  // Custom widget order - prioritize important widgets
  const getWidgetOrder = (widgetType) => {
    const priorityOrder = {
      // Top priority widgets (as requested)
      'heroHeader': 1,
      'simplicisticHeader': 2,  // Note: it's "simplicisticHeader" not "simplisticHeader"
      'simplicisticQuote': 3,   // Note: it's "simplicisticQuote" not "simplisticQuote"
      'videoTeaser': 4,
      'simplisticVideo': 5,
      'spiritualFeatureBanner': 6,
      'bulletPointsWidget': 7,
      'supremeTestimonial': 8,  // Supreme Testimonial über Infinite Banner
      'InfiniteBanner': 9,      // Infinite Banner nach Supreme Testimonial
      'galleryWidget': 10,      // Gallery Widget nach Infinite Banner
      
      // Footer always at the bottom
      'fancyFooter': 1000,
      
      // Everything else gets medium priority (500)
    };
    
    return priorityOrder[widgetType] || 500; // Default priority for unlisted widgets
  };

  const filteredWidgets = Object.keys(widgetTemplates)
    .filter(widgetType => {
      const { cat1, cat2 } = widgetTemplates[widgetType];
      
      // Filter nach Mode (Design/Product)
      if (currentMode === 'Design') {
        // Zeige nur Widgets die NICHT Product-Widgets sind
        return (cat1 !== 'Product' && cat2 !== 'Product') && 
               (selectedCategory === 'All' || cat1 === selectedCategory || cat2 === selectedCategory);
      } else if (currentMode === 'Product') {
        // Zeige nur Product-Widgets
        return (cat1 === 'Product' || cat2 === 'Product') && 
               (selectedCategory === 'All' || cat1 === selectedCategory || cat2 === selectedCategory);
      }
      
      return selectedCategory === 'All' || cat1 === selectedCategory || cat2 === selectedCategory;
    })
    .sort((a, b) => {
      // Sort by custom priority order
      const orderA = getWidgetOrder(a);
      const orderB = getWidgetOrder(b);
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same priority, sort alphabetically
      return widgetTemplates[a].name.localeCompare(widgetTemplates[b].name);
    });

  return (
    <div className="add-widgets-layout">
      <div className="widget-categories">
        {categories.map(category => (
          <button 
            key={category} 
            onClick={() => setSelectedCategory(category)} 
            className={`widget-cat-button category-button ${selectedCategory === category ? 'active' : ''}`}
          >
            <img 
              className={`widget-cat-icons ${selectedCategory === category ? 'selected-category-icon' : ''}`} 
              src={categoryIcons[category] || categoryIcons['All']} 
              alt={`${category} icon`} 
              style={{ verticalAlign: 'middle' }}
            />
            <p className={`widget-cat-text ${selectedCategory === category ? 'selected-category-text' : ''}`}>{category}</p> 
          </button>
        ))}
      </div>
      <div className="widget_container">
        <div className="widget-header-container">
          <div className="mode-toggle-container">
            <div className="mode-toggle">
              <button 
                className={`mode-toggle-btn ${currentMode === 'Design' ? 'active' : ''}`}
                onClick={() => setCurrentMode('Design')}
              >
                Design
              </button>
              <button 
                className={`mode-toggle-btn ${currentMode === 'Product' ? 'active' : ''}`}
                onClick={() => setCurrentMode('Product')}
              >
                Product
              </button>
              <div className={`mode-toggle-slider ${currentMode === 'Product' ? 'right' : 'left'}`}></div>
            </div>
          </div>
          
        </div>
        
        
        {/* ✅ CUSTOM TEMPLATES SECTION */}
        {currentMode === 'Design' && (isActuallyLoading || customTemplates.length > 0) && (
          <>
            {/* Custom Templates Header - Only show when loading or templates exist */}
            <h2 className="widget-section-title">
              Custom Templates 
              <span className="custom-templates-count-badge">
                {customTemplates.length}
              </span>
            </h2>
            
            {/* Loading State */}
            {isActuallyLoading && (
              <div className="custom-templates-loading-container">
                <div className="custom-templates-loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading your custom templates...</p>
                </div>
              </div>
            )}
            
            {/* Templates Grid */}
            {!isActuallyLoading && customTemplates.length > 0 && (
              <div className="custom-templates-grid">
                {customTemplates
                  .filter(template => {
                    if (selectedCategory === 'All') return true;
                    return template.widgetType === selectedCategory;
                  })
                  .map(template => (
                    <CustomTemplateButton
                      key={template.templateId}
                      template={template}
                      onAddWidget={onAddWidget}
                      onDeleteTemplate={deleteTemplate}
                      canDelete={canDeleteTemplate(template.templateId)}
                      colors={colors}
                      fonts={fonts}
                    />
                  ))
                }
              </div>
            )}
            
            {/* Standard Widgets Header - Same style */}
            <h2 className="widget-section-title">
              Standard Widgets
            </h2>
          </>
        )}
        
        {/* Show Standard Widgets Header when Custom Templates are hidden */}
        {currentMode === 'Design' && !isActuallyLoading && customTemplates.length === 0 && (
          <h2 className="widget-section-title">
            Standard Widgets
          </h2>
        )}

        {/* ✅ STANDARD WIDGETS */}
        {filteredWidgets.length > 0 ? (
          filteredWidgets.map(widgetType => <DraggableButton key={widgetType} widgetType={widgetType} onAddWidget={onAddWidget} />)
        ) : (
          <div className="no-widgets-message">
            {currentMode === 'Product' && !selectedProductId ? (
              <p>Select a product first to see available Product Widgets.</p>
            ) : (
              <p>No widgets available for this category.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AddWidgets;
